import { useCallback, useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'
import Lottie, { type LottieRefCurrentProps } from 'lottie-react'
import cloudAnimation from '../assets/animations/aws.json'
import developersAnimation from '../assets/animations/developers.json'

/**
 * The two animated icons above the Services hero heading (DMX-027).
 *
 * One cycle: both icons hold, corrupt in place, fade out, exchange positions
 * while invisible, fade back in, corrupt again, hold. Nothing ever travels
 * across the page. The swap happens behind the fade, so the reveal is that
 * they have traded sides.
 *
 * Deliberately not generalised. There is one caller and two fixed icons, so a
 * props API for animations, timings or slot count would be speculative. The
 * visual treatment lives in src/index.css under "Services hero icon swap".
 *
 * The icons are decorative: they repeat the meaning of the heading beside them
 * and add nothing for a screen reader, hence aria-hidden and no tab stop.
 */

/**
 * The single source of truth for the sequence. The CSS reads these through
 * custom properties set on the row below, because the sequencer here and the
 * rules there have to agree to the millisecond: when the two were declared
 * separately, changing only the CSS passed every test while the icons visibly
 * jumped sides at half opacity.
 *
 * Exported so tests assert against these rather than restating the numbers,
 * matching how src/lib/animatedFavicon.ts exposes its timings.
 */
export const SERVICES_HERO_TIMINGS = {
  /** How long the pair holds still between swaps. */
  REST_MS: 2600,
  /** One corruption burst. */
  GLITCH_MS: 450,
  /** The fade out, and the fade back in. */
  FADE_MS: 250,
  /** How far the trailing icon lags the leading one, so the pair feels organic. */
  STAGGER_MS: 100,
  /**
   * Escape hatch for a fade that never reports finishing.
   *
   * The swap waits for the trailing icon's transitionend rather than for a
   * duration, so this is not a timing budget, it exists only because
   * transitionend is not guaranteed to arrive (an interrupted transition, a
   * display:none ancestor, a throttled background tab). Comfortably longer than
   * the fade can legitimately take, so it never wins a race it should lose.
   */
  FADE_FALLBACK_MS: 750,
} as const

const { REST_MS, GLITCH_MS, FADE_MS, STAGGER_MS, FADE_FALLBACK_MS } = SERVICES_HERO_TIMINGS

/**
 * The classes this component toggles while the sequence runs.
 *
 * Exported because scripts/prerender.js has to strip them before snapshotting and
 * then fail the build if any survived. Renaming one here would otherwise disable
 * the reset and its guard together, leaving the guard unable to catch the very
 * failure a rename causes, which is the one thing a guard exists for. A unit test
 * asserts that script still names every one of them.
 */
export const SEQUENCE_CLASSES = [
  'icon-glitch-active',
  'icon-glitch-hidden',
  'icon-swap-reversed',
] as const

/** Long enough for the trailing icon's burst to have finished. */
const BURST_SETTLED_MS = GLITCH_MS + STAGGER_MS

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * Suppressing the sequence for this preference is not optional: it auto-starts
 * and repeats indefinitely, which engages WCAG 2.2.2 (Level A).
 *
 * Being accurate about what that does and does not buy, in the same terms
 * src/lib/animatedFavicon.ts uses: honouring the preference is not itself the
 * pause/stop/hide mechanism SC 2.2.2 asks for, so this likely does not conform
 * for a visitor who has never set it. That is a product decision rather than a
 * technical constraint.
 */
const usePrefersReducedMotion = () => {
  // Read synchronously on the first render. Initialising to false and correcting
  // in an effect would let the sequence schedule one timeout before the state
  // settled, for exactly the visitors who asked for no motion.
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () => window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false
  )

  useEffect(() => {
    const query = window.matchMedia?.(REDUCED_MOTION_QUERY)
    if (!query) return

    const onChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches)
    query.addEventListener?.('change', onChange)
    return () => query.removeEventListener?.('change', onChange)
  }, [])

  return prefersReducedMotion
}

/**
 * True while the element is on screen and the tab is foregrounded. Both checks
 * matter: a timer left running behind a scrolled-past hero or a background tab
 * burns battery for the rest of the visit.
 */
const useIsActivelyVisible = (elementRef: RefObject<HTMLElement>) => {
  const [isOnScreen, setIsOnScreen] = useState(false)
  // Read document.hidden rather than assuming true, so a page opened in a
  // background tab does not start its timers before the first visibility event.
  const [isTabVisible, setIsTabVisible] = useState(() => !document.hidden)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    // Unlike OptimizedImage, this observer is kept alive rather than
    // disconnected on first intersection, because leaving the viewport has to
    // pause the sequence.
    const observer = new IntersectionObserver(
      ([entry]) => setIsOnScreen(entry.isIntersecting),
      { rootMargin: '0px' }
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [elementRef])

  useEffect(() => {
    const onVisibilityChange = () => setIsTabVisible(!document.hidden)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  return isOnScreen && isTabVisible
}

const ServicesHeroIcons = () => {
  const rowRef = useRef<HTMLDivElement>(null)
  const developersRef = useRef<LottieRefCurrentProps | null>(null)
  const cloudRef = useRef<LottieRefCurrentProps | null>(null)
  // The trailing icon settles last, so its transition is the one worth watching.
  const trailingIconRef = useRef<HTMLDivElement>(null)

  const [isGlitching, setIsGlitching] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const [isReversed, setIsReversed] = useState(false)

  const prefersReducedMotion = usePrefersReducedMotion()
  const isActivelyVisible = useIsActivelyVisible(rowRef)

  /** Replays each icon's own animation once, as it arrives in its new slot. */
  const replayIcons = useCallback(() => {
    developersRef.current?.goToAndPlay(0, true)
    cloudRef.current?.goToAndPlay(0, true)
  }, [])

  useEffect(() => {
    if (prefersReducedMotion || !isActivelyVisible) return

    let isCancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    let detachFadeListener: (() => void) | undefined

    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms)
      })

    /**
     * Resolves once the trailing icon's opacity transition has actually ended.
     *
     * Waiting a fixed duration here was only ever an estimate. A CSS transition
     * starts at the first paint after React commits, which is strictly later
     * than the timer that scheduled it, and under CPU throttle that gap grew
     * until the icon was still visible when the swap landed, measured jumping
     * 112px at opacity 0.167. Listening for the event makes "faded out before
     * swapped" an ordering guarantee instead of a margin that can be eaten.
     */
    const waitForFadeOut = () =>
      new Promise<void>((resolve) => {
        const element = trailingIconRef.current
        if (!element) {
          timer = setTimeout(resolve, FADE_FALLBACK_MS)
          return
        }

        const onTransitionEnd = (event: TransitionEvent) => {
          if (event.propertyName !== 'opacity') return
          detachFadeListener?.()
          resolve()
        }

        element.addEventListener('transitionend', onTransitionEnd)
        const fallback = setTimeout(() => {
          detachFadeListener?.()
          resolve()
        }, FADE_FALLBACK_MS)
        timer = fallback

        detachFadeListener = () => {
          element.removeEventListener('transitionend', onTransitionEnd)
          clearTimeout(fallback)
          detachFadeListener = undefined
        }
      })

    // Every await is followed by a cancellation check so the loop cannot
    // outlive the effect. Without it, a component unmounted mid-cycle would
    // keep setting state and keep scheduling timeouts.
    const run = async () => {
      while (!isCancelled) {
        await wait(REST_MS)
        if (isCancelled) return

        setIsGlitching(true)
        await wait(BURST_SETTLED_MS)
        if (isCancelled) return
        setIsGlitching(false)

        setIsHidden(true)
        await waitForFadeOut()
        if (isCancelled) return

        setIsReversed((reversed) => !reversed)
        setIsHidden(false)
        replayIcons()
        await wait(FADE_MS + STAGGER_MS)
        if (isCancelled) return

        setIsGlitching(true)
        await wait(BURST_SETTLED_MS)
        if (isCancelled) return
        setIsGlitching(false)
      }
    }

    void run()

    return () => {
      isCancelled = true
      if (timer !== undefined) clearTimeout(timer)
      detachFadeListener?.()
      // Return to a resting state on unmount, so a client-side navigation back
      // to this route never restores mid-sequence classes. isReversed is left
      // alone: a reversed pair is itself a legitimate resting state.
      //
      // This does NOT protect the prerender. That snapshot is taken from a live,
      // mounted page, so this cleanup never runs during it, so the reset and the
      // build-failing guard for that live in scripts/prerender.js. Note that only
      // the classes are restored there: each Lottie is serialised at whatever
      // frame it had reached, so the snapshot shows resting *state* but not
      // necessarily resting artwork. That is how all 60-odd Lottie instances on
      // the site already prerender.
      setIsGlitching(false)
      setIsHidden(false)
    }
  }, [prefersReducedMotion, isActivelyVisible, replayIcons])

  const iconClassName = (isTrailing: boolean) =>
    [
      'icon-glitch w-20 h-20',
      isGlitching ? 'icon-glitch-active' : '',
      isHidden ? 'icon-glitch-hidden' : '',
      isTrailing ? 'icon-glitch-delayed' : '',
    ]
      .filter(Boolean)
      .join(' ')

  // Feeding the CSS from SERVICES_HERO_TIMINGS keeps one set of numbers. These
  // are static, so they add nothing dynamic to the prerendered markup.
  const timingProperties: CSSProperties & Record<`--${string}`, string> = {
    '--icon-glitch-ms': `${GLITCH_MS}ms`,
    '--icon-fade-ms': `${FADE_MS}ms`,
    '--icon-stagger-ms': `${STAGGER_MS}ms`,
  }

  return (
    <div
      ref={rowRef}
      aria-hidden="true"
      style={timingProperties}
      className={['flex items-center justify-center gap-8 mb-5', isReversed ? 'icon-swap-reversed' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className={iconClassName(false)}>
        {/* The dark-mode inversion has to sit on an inner element: CSS `filter`
            is a single property, so sharing it with the glitch's drop-shadows
            would mean one silently overwriting the other. */}
        <div className="dark:invert dark:brightness-0 dark:contrast-200 w-full h-full">
          <Lottie
            lottieRef={developersRef}
            animationData={developersAnimation}
            loop={false}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
            rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
          />
        </div>
      </div>

      <div ref={trailingIconRef} className={iconClassName(true)}>
        <div className="dark:invert dark:brightness-0 dark:contrast-200 w-full h-full">
          <Lottie
            lottieRef={cloudRef}
            animationData={cloudAnimation}
            loop={false}
            autoplay={true}
            style={{ width: '100%', height: '100%' }}
            rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
          />
        </div>
      </div>
    </div>
  )
}

export default ServicesHeroIcons
